import axios from 'axios';
import { toast } from 'react-toastify';
import { getApiBaseUrl, getClientMetadata } from '../platform/api';
import { authStorage } from '../platform/storage';
import { API_FAILURE, API_TIMEOUT_MS, classifyApiFailure } from '../platform/apiPolicy.mjs';
import { sameSessionToken } from '../platform/sessionTokens.mjs';

let authFailureHandler = () => {};
let biometricLoginHandler = () => {};
export const setBiometricLoginHandler = handler => { biometricLoginHandler = handler; };

export const setAuthFailureHandler = (handler) => {
    authFailureHandler = typeof handler === 'function' ? handler : () => {};
};

// Create an axios instance with common configuration
const baseURL = getApiBaseUrl();
const api = axios.create({ baseURL, timeout: API_TIMEOUT_MS });
// Add request interceptor for development delay
if (import.meta.env.MODE === 'development') {
    api.interceptors.request.use(
        config => {
            return new Promise(resolve => {
                setTimeout(() => resolve(config), 400);
            });
        }
    );
}
// Add response interceptor
api.interceptors.response.use(
    response => response,
    async error => {
        if (error.config?.signal?.aborted) return Promise.reject(new axios.CanceledError());
        if (authStorage.refreshSession && error.config?._nativeAuth && error.response?.status === 401 &&
            error.response.data?.code === 'token_expired' && !error.config?._nativeRetry) {
            const oldToken = error.config?.headers?.Authorization?.replace(/^Bearer /, '');
            let token;
            try { token = oldToken && await authStorage.refreshSession(oldToken); }
            catch (refreshError) {
                if (error.config?.signal?.aborted) return Promise.reject(new axios.CanceledError());
                if (refreshError.code === 'BIOMETRIC_LOCKED') biometricLoginHandler();
                if (refreshError.response?.status === 401) authFailureHandler();
                return Promise.reject(refreshError);
            }
            if (token) {
                error.config._nativeRetry = true;
                error.config.headers.Authorization = `Bearer ${token}`;
                return api.request(error.config);
            }
        }
        if (error.code === 'BIOMETRIC_LOCKED') biometricLoginHandler();
        if (error.code?.startsWith('BIOMETRIC_') || error.code === 'SESSION_REFRESH_UNAVAILABLE') return Promise.reject(error);
        error.connectFailureType = classifyApiFailure(error);
        // Axios errors include request bodies and auth headers. Log only safe diagnostics.
        console.warn('API request failed:', {
            type: error.connectFailureType,
            status: Number.isInteger(error.response?.status) ? error.response.status : null
        });
        // Handle token expiration or invalid token
        if (error.connectFailureType === API_FAILURE.AUTHENTICATION &&
            error.config?.headers?.Authorization !== '') {
            // Check if the error message specifically indicates token expiration
            const isExpired = error.response.data &&
                (error.response.data.expired ||
                    error.response.data.code === 'token_expired' ||
                    error.response.data.message === 'Token expired');

            if (isExpired) {
                toast.error('Your session has expired. Please log in again.');
            } else {
                toast.error('Authentication error. Please log in again.');
            }

            authFailureHandler();
        }
        return Promise.reject(error);
    }
);

// Add request interceptor to automatically add token to requests
api.interceptors.request.use(
    async config => {
        const hasExplicitAuth = config.headers.Authorization !== undefined;
        if (!hasExplicitAuth && authStorage.refreshSession) config._nativeAuth = true;
        // Do not replay an operation after logout/lock or account switching.
        if (config._nativeAuth && hasExplicitAuth) {
            const current = await authStorage.get('token');
            if (`Bearer ${current}` !== config.headers.Authorization) {
                throw Object.assign(new Error('Session changed.'), { code: 'BIOMETRIC_CANCELLED' });
            }
        }
        const token = hasExplicitAuth ? null : await authStorage.get('token');
        // Resolve renewed credentials without moving an identity-scoped request
        // to another login. The anchor can be an older token from this session.
        if (config.sessionToken !== undefined &&
            !sameSessionToken(config.sessionToken, hasExplicitAuth
                ? config.headers.Authorization?.replace(/^Bearer /, '') : token)) {
            throw new axios.CanceledError('Session changed.');
        }
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        const client = getClientMetadata();
        config.headers['X-Connect-Platform'] = client.platform;
        config.headers['X-Connect-Version'] = client.version;
        return config;
    },
    error => Promise.reject(error)
);

export default api;
